<script setup lang="ts">
import { Donut } from '@unovis/ts'
import { VisDonut, VisSingleContainer, VisTooltip } from '@unovis/vue'

const teamsStore = useTeamsStore()
const { t } = useI18n()
const localePath = useLocalePath()

const credits = computed(() => {
  return teamsStore.currentTeam?.credits || 0
})

const creditsPack = computed(() => {
  return teamsStore.currentTeam?.creditsPack || 0
})

// Chart sizing
const chartRef = useTemplateRef<HTMLElement>('chartRef')
const chartSize = useElementSize(chartRef)

// Calculate chart data
const chartData = computed(() => {
  const currentCredits = credits.value
  const monthlyPack = creditsPack.value

  if (monthlyPack === 0) {
    return []
  }

  const data = []

  // Determine if this is a free trial (500 credits or less)
  const isFreeTrial = monthlyPack <= 500
  const packType = isFreeTrial ? 'freeTrial' : 'monthly'
  const packName = isFreeTrial ? t('credits.freeTrial') : t('credits.monthlyPack')

  // If credits exceed monthly pack, show one-time credits separately
  if (currentCredits > monthlyPack) {
    const oneTimeCredits = currentCredits - monthlyPack

    data.push({
      color: '#6366f1', // green-500
      count: monthlyPack,
      name: packName,
      type: packType,
      value: monthlyPack,
    })

    data.push({
      color: '#a855f7', // blue-500
      count: oneTimeCredits,
      name: t('credits.oneTimeCredits'),
      type: 'onetime',
      value: oneTimeCredits,
    })
  }
  else {
    // Show remaining vs used from monthly pack
    const remaining = currentCredits
    const used = monthlyPack - currentCredits

    if (remaining > 0) {
      data.push({
        color: '#6366f1', // indigo-500
        count: remaining,
        name: t('credits.remaining'),
        type: 'remaining',
        value: remaining,
      })
    }

    if (used > 0) {
      data.push({
        color: '#EF4444', // red-500
        count: used,
        name: t('credits.used'),
        type: 'used',
        value: used,
      })
    }
  }

  return data
})

// Chart configuration
const chartConfig = {
  arcWidth: 30,
  cornerRadius: 4,
  padAngle: 0.02,
}

// Color accessor
function colorAccessor(dataPoint: any) {
  return dataPoint.color
}

// Label accessor
function labelAccessor(dataPoint: any) {
  return `${dataPoint.name}: ${formatNumber(dataPoint.count)}`
}

// Tooltip content
function tooltipContent(segment: any) {
  const data = segment.data
  const formattedCount = numbro(data.count)

  let description = ''

  switch (data.type) {
    case 'monthly':
      description = t('credits.monthlyPackDescription')
      break
    case 'freeTrial':
      description = t('credits.freeTrialDescription')
      break
    case 'onetime':
      description = t('credits.oneTimeDescription')
      break
    case 'remaining':
      description = t('credits.remainingDescription')
      break
    case 'used':
      description = t('credits.usedDescription')
      break
  }

  return `
    <div class="font-sans bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-0 max-w-xs z-20">
      <div class="flex items-center gap-4 px-3 pt-3 pb-2 border-b border-gray-100 dark:border-gray-700">
        <div class="w-3 h-3 rounded-full flex-shrink-0" style="background-color: ${data.color};"></div>
        <div class="font-semibold text-sm text-gray-900 dark:text-gray-100">${data.name}</div>
      </div>
      <div class="px-3 pt-2 pb-3">
        <div class="flex justify-between mb-1 text-sm">
          <span class="text-gray-500 dark:text-gray-400 mr-3">${t('credits.amount')}:</span>
          <span class="text-gray-900 dark:text-gray-100 font-medium">${formattedCount}</span>
        </div>
        <div class="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-400">
          ${description}
        </div>
      </div>
    </div>
  `
}

const triggers = { [Donut.selectors.segment]: tooltipContent }

// Central label shows total credits
const centralLabel = computed(() => {
  return numbro(credits.value)
})

const centralSubLabel = computed(() => {
  return t('credits.totalCredits')
})
</script>

<template>
  <UCard
    class="gradient-texture"
    :ui="{ body: { base: '!p-4' } }"
  >
    <template #header>
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-4">
          <UIcon
            class="text-lg text-gray-500 md:text-xl dark:text-gray-400"
            :name="getIcon('creditCard')"
          />

          <h2 class="text-lg font-bold">
            {{ t('credits.title') }}
          </h2>
        </div>
      </div>
    </template>

    <div class="grid h-full grid-cols-1 gap-4 lg:grid-cols-2">
      <!-- Chart -->
      <div class="relative flex min-h-64 items-center justify-center rounded-lg">
        <AnimationDotsPattern class="absolute inset-0" />

        <ClientOnly v-if="chartData.length > 0">
          <div
            ref="chartRef"
            class="relative z-10 flex size-full items-center justify-center p-4"
          >
            <VisSingleContainer
              class="donut-container flex size-full items-center justify-center transition-none"
              :data="chartData"
              :height="Math.max(chartSize.height.value, 256)"
              :width="chartSize.width.value"
            >
              <VisDonut
                :arc-width="chartConfig.arcWidth"
                :central-label="centralLabel"
                :central-sub-label="centralSubLabel"
                :color="colorAccessor"
                :corner-radius="chartConfig.cornerRadius"
                :duration="0"
                :label="labelAccessor"
                :pad-angle="chartConfig.padAngle"
                show-background
                :value="(datapoint: any) => datapoint.value"
              />

              <VisTooltip
                :offset="[10, 10]"
                sticky
                :triggers="triggers"
              />
            </VisSingleContainer>
          </div>
        </ClientOnly>

        <div
          v-else
          class="absolute inset-0 z-10 flex items-center justify-center bg-white/20 dark:bg-gray-900/20"
        >
          <p class="text-gray-500">
            {{ t('credits.noPackConfigured') }}
          </p>
        </div>
      </div>

      <!-- Stats -->
      <div class="flex w-full flex-col gap-4">
        <div class="grid grid-cols-2 gap-4">
          <UTooltip
            :popper="{ placement: 'bottom' }"
            :text="numbro(credits)"
          >
            <div class="w-full rounded-lg bg-gray-50 p-4 dark:bg-gray-800/20">
              <div class="text-sm text-gray-600 dark:text-gray-400">
                {{ t('credits.currentCredits') }}
              </div>

              <div class="text-2xl font-bold">
                {{ formatNumber(credits) }}
              </div>
            </div>
          </UTooltip>

          <UTooltip
            :popper="{ placement: 'bottom' }"
            :text="numbro(creditsPack)"
          >
            <div class="w-full rounded-lg bg-gray-50 p-4 dark:bg-gray-800/20">
              <div class="text-sm text-gray-600 dark:text-gray-400">
                {{ creditsPack <= 500 ? t('credits.freeTrial') : t('credits.monthlyPack') }}
              </div>

              <div class="text-2xl font-bold">
                {{ formatNumber(creditsPack) }}
              </div>
            </div>
          </UTooltip>
        </div>

        <!-- Additional info when credits exceed pack -->
        <div
          v-if="credits > creditsPack && creditsPack > 0"
          class="rounded-lg bg-blue-50/20 p-4 dark:bg-blue-950/20"
        >
          <div class="flex items-center gap-4">
            <UIcon
              class="text-blue-600 dark:text-blue-400"
              name="i-heroicons-gift-20-solid"
            />

            <div>
              <div class="text-sm font-medium text-blue-900 dark:text-blue-100">
                {{ t('credits.oneTimeCreditsActive') }}
              </div>

              <div class="text-xs text-blue-700 dark:text-blue-300">
                {{ t('credits.extraCredits', { amount: formatNumber(credits - creditsPack) }) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Subscribe button for free tier users -->
        <div
          v-else-if="creditsPack <= 500"
          class="w-full rounded-lg bg-gradient-to-r from-blue-50 to-purple-50 p-4 ring-1 ring-inset ring-blue-500/10 dark:from-blue-950/30 dark:to-purple-950/30 dark:ring-blue-950/30"
        >
          <div class="flex w-full flex-col items-center justify-center gap-4">
            <div class="flex items-center gap-4 text-balance text-center">
              <div>
                <div class="text-sm font-medium text-blue-900 dark:text-blue-100">
                  {{ t('credits.upgradeTitle') }}
                </div>

                <div class="text-balance text-xs text-blue-700 dark:text-blue-300">
                  {{ t('credits.upgradeDescription') }}
                </div>
              </div>
            </div>

            <UButton
              block
              color="indigo"
              size="sm"
              :to="localePath('/settings/subscription')"
              :trailing-icon="getIcon('arrowRight')"
              variant="subtle"
            >
              {{ t('credits.subscribe') }}
            </UButton>
          </div>
        </div>

        <!-- Subscription form for subscribed users -->
        <div v-else-if="creditsPack > 500">
          <SubscriptionFormCredits />
        </div>
      </div>
    </div>
  </UCard>
</template>

<style lang="postcss">
.donut-container {
  /* Central label styling */
  --vis-donut-central-label-font-size: 20px;
  --vis-donut-central-label-font-weight: 700;
  --vis-donut-central-label-text-color: theme('colors.gray.900');

  /* Sub-label styling */
  --vis-donut-central-sub-label-font-size: 12px;
  --vis-donut-central-sub-label-font-weight: 500;
  --vis-donut-central-sub-label-text-color: theme('colors.gray.600');

  /* Background styling */
  --vis-donut-background-color: theme('colors.gray.100');
  --vis-tooltip-padding: 0;
  --vis-tooltip-background-color: transparent;
  --vis-tooltip-border-color: transparent;
}

html.dark {
  .donut-container {
    --vis-donut-central-label-text-color: theme('colors.gray.50');
    --vis-donut-central-sub-label-text-color: theme('colors.gray.400');
    --vis-donut-background-color: theme('colors.gray.800');
  }
}
</style>

<i18n lang="json">
{
  "en": {
    "credits": {
      "amount": "Amount",
      "currentCredits": "Current credits",
      "extraCredits": "+{amount} extra credits",
      "freeTrial": "Free trial",
      "freeTrialDescription": "Your initial credit allowance",
      "monthlyPack": "Monthly pack",
      "monthlyPackDescription": "Your monthly credit allowance",
      "noPackConfigured": "No credit pack configured",
      "oneTimeCredits": "One-time credits",
      "oneTimeCreditsActive": "One-time credits active",
      "oneTimeDescription": "Additional credits beyond your monthly pack",
      "packUsage": "Pack usage",
      "remainingDescription": "Credits remaining from your monthly pack",
      "subscribe": "Subscribe",
      "title": "Your credits",
      "totalCredits": "Total credits",
      "upgradeDescription": "Get more credits with a subscription plan",
      "upgradeTitle": "Upgrade your plan",
      "used": "Used",
      "usedDescription": "Credits used from your monthly pack"
    }
  },
  "es": {
    "credits": {
      "amount": "Cantidad",
      "currentCredits": "Créditos actuales",
      "extraCredits": "+{amount} créditos extra",
      "freeTrial": "Prueba gratuita",
      "freeTrialDescription": "Tu asignación inicial de créditos",
      "monthlyPack": "Paquete mensual",
      "monthlyPackDescription": "Tu asignación mensual de créditos",
      "noPackConfigured": "No hay paquete de créditos configurado",
      "oneTimeCredits": "Créditos únicos",
      "oneTimeCreditsActive": "Créditos únicos activos",
      "oneTimeDescription": "Créditos adicionales más allá de tu paquete mensual",
      "packUsage": "Uso del paquete",
      "remainingDescription": "Créditos restantes de tu paquete mensual",
      "subscribe": "Suscribirse",
      "title": "Tus créditos",
      "totalCredits": "Créditos totales",
      "upgradeDescription": "Obtén más créditos con un plan de suscripción",
      "upgradeTitle": "Actualizar tu plan",
      "used": "Usados",
      "usedDescription": "Créditos usados de tu paquete mensual"
    }
  },
  "fr": {
    "credits": {
      "amount": "Montant",
      "currentCredits": "Crédits actuels",
      "extraCredits": "+{amount} crédits supplémentaires",
      "freeTrial": "Essai gratuit",
      "freeTrialDescription": "Votre allocation initiale de crédits",
      "monthlyPack": "Pack mensuel",
      "monthlyPackDescription": "Votre allocation mensuelle de crédits",
      "noPackConfigured": "Aucun pack de crédits configuré",
      "oneTimeCredits": "Crédits ponctuels",
      "oneTimeCreditsActive": "Crédits ponctuels actifs",
      "oneTimeDescription": "Crédits supplémentaires au-delà de votre pack mensuel",
      "packUsage": "Utilisation du Pack",
      "remainingDescription": "Crédits restants de votre pack mensuel",
      "subscribe": "S'abonner",
      "title": "Vos crédits",
      "totalCredits": "Crédits totaux",
      "upgradeDescription": "Obtenez plus de crédits avec un plan d'abonnement",
      "upgradeTitle": "Passer à un plan plus élevé",
      "used": "Utilisés",
      "usedDescription": "Crédits utilisés de votre pack mensuel"
    }
  }
}
</i18n>
